import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Upload, X } from "lucide-react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import { useTaxonomy } from "../hooks/useTaxonomy";
import { isDividerEntry } from "../utils/taxonomyMap"; // 110 §3a
import { SCHED_SLOTS, SCHED_START, slotToTime } from "../utils";
import { ModalLayout } from "../components/SharedUI";
import { RAD, FS } from "../lib/tokens";
import MenuSelect from "./MenuSelect"; // 154B §2
import { useConfirm } from "./useConfirmAndUndo";

// Phase 19 — per-place catalog admin + base-availability matrix editor.
// Mounted from SettingsView under its own collapsible. Gated by the
// caller via sg('lateral.places.edit'); panel content assumes write
// authority (server enforces independently).
//
// baseAvailability shape (matches /api/places contract):
//   { [dow 0..6]: number[] }  — slot indices (0..25) where the place is
//   AVAILABLE on that weekday. The grid renders the rest as dimmed.
//   null / missing object → fully available, every day, every slot.
//   missing dow key (object present) → fully available on that dow.
//   empty array → never available on that dow.

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DOW_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function isDowFullyAvailable(dowArr: any) {
  if (!Array.isArray(dowArr)) return true;
  return dowArr.length === SCHED_SLOTS;
}

function fullDow() {
  return Array.from({ length: SCHED_SLOTS }, (_, i) => i);
}

// Normalize the matrix back down to null if every dow is fully available.
// Keeps DB state lean and consistent with the "missing object = always
// available" convention.
function normalizeBaseAvailability(matrix: any) {
  if (!matrix) return null;
  const allFull = [0, 1, 2, 3, 4, 5, 6].every((d) =>
    isDowFullyAvailable(matrix[d]),
  );
  return allFull ? null : matrix;
}

function expandToFullMatrix(matrix: any) {
  // For the editor: convert {null|partial} → fully-populated {0..6: [...]}
  // so click-to-toggle has a stable starting point.
  const out: Record<number, any> = {};
  for (let d = 0; d < 7; d++) {
    if (!matrix || !(d in matrix)) {
      out[d] = fullDow();
    } else if (Array.isArray(matrix[d])) {
      out[d] = matrix[d].slice();
    } else {
      out[d] = fullDow();
    }
  }
  return out;
}

type BaseAvailabilityEditorProps = {
  place?: any;
  value?: any;
  onClose?: any;
  onSave?: any;
};
function BaseAvailabilityEditor({ place, value, onClose, onSave }: BaseAvailabilityEditorProps) {
  const { t } = useTranslation();
  const [matrix, setMatrix] = useState<any>(() => expandToFullMatrix(value));
  const [busy, setBusy] = useState(false);

  const toggleSlot = (dow: any, slot: any) => {
    setMatrix((m: any) => {
      const cur = m[dow] || [];
      const has = cur.includes(slot);
      const next = has ? cur.filter((s: any) => s !== slot) : [...cur, slot].sort((a: any, b: any) => a - b);
      return { ...m, [dow]: next };
    });
  };
  const setDow = (dow: any, full: any) => {
    setMatrix((m: any) => ({ ...m, [dow]: full ? fullDow() : [] }));
  };
  const setAll = (full: any) => {
    const next: Record<number, any> = {};
    for (let d = 0; d < 7; d++) next[d] = full ? fullDow() : [];
    setMatrix(next);
  };
  const save = async () => {
    setBusy(true);
    try {
      const normalized = normalizeBaseAvailability(matrix);
      await onSave(normalized);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalLayout
      title={`${t("places.baseAvail.title", { fallback: "Base availability" })} — ${place.name}`}
      onClose={onClose}
      maxWidth={460}
      {...({} as any)}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 16px", borderTop: "1px solid var(--br)" }}>
          <button className="btn btn-ghost" type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel", { fallback: "Cancel" })}
          </button>
          <button className="btn btn-primary" type="button" onClick={save} disabled={busy} style={{ padding: "4px 12px", fontSize: FS.s11 }}>
            {t("common.save", { fallback: "Save" })}
          </button>
        </div>
      }
    >
      <p style={{ fontSize: FS.s11, color: "var(--tm)", margin: "0 0 12px" }}>
        {t("places.baseAvail.desc", {
          fallback:
            "Highlighted = available. Click to toggle. Dimmed slots will show under reservations on the grid. Empty = fully available.",
        })}
      </p>
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <button className="btn btn-ghost" type="button" onClick={() => setAll(true)}>
          {t("places.baseAvail.allOn", { fallback: "All available" })}
        </button>
        <button className="btn btn-ghost" type="button" onClick={() => setAll(false)}>
          {t("places.baseAvail.allOff", { fallback: "All unavailable" })}
        </button>
      </div>
      {/* Transposed to match the reservations grid: hours run vertically
          down the side, days across the top. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `48px repeat(7, 1fr)`,
          gridTemplateRows: `auto repeat(${SCHED_SLOTS}, 16px)`,
          gap: 0,
          background: "var(--bg)",
          border: "1px solid var(--br)",
          fontSize: FS.s9,
        }}
      >
        {/* Corner + day-of-week headers (each toggles the whole day) */}
        <div style={{ background: "var(--bg)", borderBottom: "1px solid var(--br)" }} />
        {DOW_LABELS.map((label, d) => {
          const dowArr = matrix[d] || [];
          return (
            <button
              key={"dh-" + d}
              type="button"
              onClick={() => setDow(d, dowArr.length !== SCHED_SLOTS)}
              style={{
                background: "var(--bg)",
                color: "var(--tm)",
                border: "none",
                borderBottom: "1px solid var(--br)",
                borderLeft: "1px solid rgba(128,128,128,0.10)",
                padding: 4,
                fontSize: FS.s10,
                cursor: "pointer",
                textAlign: "center",
              }}
              title={t("places.baseAvail.toggleDow", {
                fallback: "Click to toggle the whole day",
              })}
            >
              {t("places.dow." + DOW_KEYS[d], { fallback: label })}
            </button>
          );
        })}
        {/* Hour rows: time label + 7 day toggle cells */}
        {Array.from({ length: SCHED_SLOTS }, (_, s) => {
          const isHour = s % 2 === 0;
          return (
            <div key={"row-" + s} style={{ display: "contents" }}>
              <div
                style={{
                  background: "var(--bg)",
                  color: "var(--tm)",
                  fontSize: FS.s8,
                  textAlign: "right",
                  paddingRight: 4,
                  borderBottom: isHour ? "1px solid var(--br)" : "1px solid rgba(128,128,128,0.12)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {isHour ? SCHED_START + s / 2 : ""}
              </div>
              {DOW_LABELS.map((label, d) => {
                const on = (matrix[d] || []).includes(s);
                return (
                  <button
                    key={"c-" + d + "-" + s}
                    type="button"
                    onClick={() => toggleSlot(d, s)}
                    title={`${t("places.dow." + DOW_KEYS[d], { fallback: label })} ${slotToTime(s)} — ${on ? t("places.available", { fallback: "available" }) : t("places.unavailable", { fallback: "unavailable" })}`}
                    style={{
                      background: on ? "var(--ac)" : "var(--bg)",
                      opacity: on ? 1 : 0.45,
                      border: "none",
                      borderBottom: isHour ? "1px solid var(--br)" : "1px solid rgba(128,128,128,0.12)",
                      borderLeft: "1px solid rgba(128,128,128,0.10)",
                      padding: 0,
                      cursor: "pointer",
                    }}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </ModalLayout>
  );
}

export default function PlacesAdminPanel({ toast }: { toast?: any }) {
  const { t } = useTranslation();
  const askConfirm = useConfirm();
  const [places, setPlaces] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any>(null); // baseAvail editor target
  const [draft, setDraft] = useState<any>(null); // new-place form
  // Pre-edit value per `${id}:${field}`, captured on focus. The onChange
  // handlers mutate `places` live for display, so comparing against the
  // current `p.value` at blur always matched (the rename-never-persists bug);
  // we compare against this focus-time snapshot instead.
  const editOrig = useRef<Record<string, any>>({});

  const { entries: catEntries } = useTaxonomy("place_category", { includeArchived: false });
  const { entries: locEntries } = useTaxonomy("place_location", { includeArchived: false });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/places?includeArchived=1");
      setPlaces(Array.isArray(data) ? data : []);
    } catch (e) {
      toast && toast(t("places.load_failed", { fallback: "Failed to load places" }), "error");
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const grouped = useMemo(() => {
    const byCat = new Map();
    for (const p of places) {
      if (!byCat.has(p.categoryCode)) byCat.set(p.categoryCode, []);
      byCat.get(p.categoryCode).push(p);
    }
    return Array.from(byCat.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [places]);

  const startNew = () => {
    setDraft({
      name: "",
      /* 110 §3a — the first ENTRY may be a divider, and a divider is never a
         value: defaulting to it would commit `__div__…` on save. */
      categoryCode:
        catEntries.find(
          (c: { code?: string; meta?: Record<string, unknown> | null }) =>
            !isDividerEntry(c),
        )?.code || "",
      locationCode: "",
      capacity: "",
      notes: "",
    });
  };

  const saveNew = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      toast && toast(t("places.name_required", { fallback: "Name is required" }), "warning");
      return;
    }
    if (!draft.categoryCode) {
      toast && toast(
        t("places.category_required", { fallback: "Category is required" }),
        "warning",
      );
      return;
    }
    try {
      await api.post("/places", {
        name: draft.name.trim(),
        categoryCode: draft.categoryCode,
        locationCode: draft.locationCode || null,
        capacity: draft.capacity === "" ? null : Number(draft.capacity),
        notes: draft.notes || null,
      });
      setDraft(null);
      refresh();
      toast && toast(t("places.created_ok", { fallback: "Place created" }), "success");
    } catch (e) {
      toast && toast(t("places.create_failed", { fallback: "Couldn't create the place — please try again." }), "error");
    }
  };

  const patchPlace = async (id: any, patch: any) => {
    try {
      const updated = await api.patch(`/places/${id}`, patch);
      setPlaces((arr) => arr.map((p) => (p.id === id ? updated : p)));
    } catch (e) {
      toast && toast(t("places.save_failed", { fallback: "Couldn't save — please try again." }), "error");
    }
  };

  // Reuse the firm-logo image-upload idiom (FileReader → data URL, 500KB
  // cap). Place.photo is a String column, so the data URL is stored inline.
  const onPhoto = (p: any, e: any) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 512000) {
      toast && toast(t("places.photo_too_large", { fallback: "Image too large (max 500KB)" }), "error");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      patchPlace(p.id, { photo: (ev.target as any).result });
      toast && toast(t("places.photo_uploaded", { fallback: "Photo uploaded" }), "success");
    };
    reader.readAsDataURL(file);
  };

  const deletePlace = async (id: any) => {
    askConfirm({
      // 161C §3
      title: t("confirm.delete_place_title", { fallback: "Delete the place?" }),
      message: t("places.confirm_delete", { fallback: "Delete this place and all its reservations?" }),
      confirmLabel: t("common.delete", { fallback: "Delete" }),
      onConfirm: async () => {
        try {
          await api.del(`/places/${id}`);
          setPlaces((arr) => arr.filter((p) => p.id !== id));
          toast && toast(t("places.deleted_ok", { fallback: "Place deleted" }), "success");
        } catch (e) {
          toast && toast(t("places.delete_failed", { fallback: "Couldn't delete the place — please try again." }), "error");
        }
      },
    });
  };

  const labelFor = (entries: any, code: any) => {
    const e = entries.find((x: any) => x.code === code);
    return e ? e.labelEn || e.label || e.code : code;
  };

  return (
    <div>
      <p style={{ fontSize: FS.s11, color: "var(--tm)", margin: "4px 0 10px", lineHeight: 1.5 }}>
        {t("places.adminDesc", {
          fallback:
            "Reservable places. Each place belongs to one category — that drives the grid it appears in. Base availability sets the per-day, per-hour underlay shown on the grid.",
        })}
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <button className="btn btn-primary" type="button" onClick={startNew} disabled={!!draft || !catEntries.length} style={{ padding: "4px 12px", fontSize: FS.s11 }}>
          + {t("places.new", { fallback: "New place" })}
        </button>
        {!catEntries.length && (
          <span style={{ fontSize: FS.s11, color: "var(--am)", alignSelf: "center" }}>
            {t("places.no_categories", {
              fallback:
                "Define at least one place_category in Custom Lists before adding places.",
            })}
          </span>
        )}
      </div>

      {draft && (
        <div
          style={{
            border: "1px solid var(--br)",
            background: "var(--bg2, var(--bg))",
            borderRadius: RAD.r6,
            padding: 10,
            marginBottom: 12,
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 6,
          }}
        >
          <input autoComplete="off" /* 207 §18 */
            className="field-input no-contact-autofill"
            placeholder={t("places.field.name", { fallback: "Name" })}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            style={{ gridColumn: "span 2" }}
          />
          <MenuSelect
            className="field-input"
            value={draft.categoryCode}
            onChange={(v) => setDraft({ ...draft, categoryCode: v })}
            options={[
              ["", t("places.field.category", { fallback: "Category" })] as const,
              /* 110 §3a's divider is 108 §6b's row now */
              ...catEntries.map(
                (c: any) =>
                  [
                    String(c.code ?? ""),
                    String(c.labelEn || c.label || c.code || ""),
                  ] as const,
              ),
            ]}
          />
          <MenuSelect
            className="field-input"
            value={draft.locationCode}
            onChange={(v) => setDraft({ ...draft, locationCode: v })}
            options={[
              ["", t("places.field.location", { fallback: "Location" })] as const,
              /* 110 §3a's divider is 108 §6b's row now */
              ...locEntries.map(
                (c: any) =>
                  [
                    String(c.code ?? ""),
                    String(c.labelEn || c.label || c.code || ""),
                  ] as const,
              ),
            ]}
          />
          <input
            className="field-input"
            type="number"
            min={0}
            placeholder={t("places.field.capacity", { fallback: "Capacity" })}
            value={draft.capacity}
            onChange={(e) => setDraft({ ...draft, capacity: e.target.value })}
          />
          <input
            className="field-input"
            placeholder={t("places.field.notes", { fallback: "Notes (optional)" })}
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            style={{ gridColumn: "span 4" }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn btn-ghost" type="button" onClick={() => setDraft(null)}>
              {t("common.cancel", { fallback: "Cancel" })}
            </button>
            <button className="btn btn-primary" type="button" onClick={saveNew} style={{ padding: "4px 12px", fontSize: FS.s11 }}>
              {t("common.add", { fallback: "Add" })}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: FS.s11, color: "var(--tm)" }}>{t("common.loading", { fallback: "Loading…" })}</div>
      ) : !places.length ? (
        <div style={{ fontSize: FS.s11, color: "var(--tm)" }}>
          {t("places.empty", { fallback: "No places yet." })}
        </div>
      ) : (
        grouped.map(([catCode, list]) => (
          <div key={catCode} style={{ marginBottom: 14 }}>
            <div
              style={{
                fontSize: FS.s11,
                fontWeight: 600,
                color: "var(--ts)",
                margin: "6px 0",
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              {labelFor(catEntries, catCode)} ({list.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {list.map((p: any) => (
                <div
                  key={p.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.2fr 1fr 0.6fr 1.4fr 0.4fr auto",
                    gap: 6,
                    alignItems: "center",
                    padding: 6,
                    border: "1px solid var(--br)",
                    borderRadius: RAD.r5,
                    background: p.archived ? "transparent" : "var(--bg)",
                    opacity: p.archived ? 0.55 : 1,
                  }}
                >
                  <input
                    className="field-input"
                    value={p.name}
                    onFocus={(e) => {
                      editOrig.current[p.id + ":name"] = e.target.value;
                    }}
                    onChange={(e) =>
                      setPlaces((arr) => arr.map((x) => (x.id === p.id ? { ...x, name: e.target.value } : x)))
                    }
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== editOrig.current[p.id + ":name"]) patchPlace(p.id, { name: v });
                    }}
                  />
                  <MenuSelect
                    className="field-input"
                    value={p.locationCode || ""}
                    onChange={(v) => patchPlace(p.id, { locationCode: v || null })}
                    options={[
                      ["", "—"] as const,
                      ...locEntries.map(
                        (c: any) =>
                          [
                            String(c.code ?? ""),
                            String(c.labelEn || c.label || c.code || ""),
                          ] as const,
                      ),
                    ]}
                  />
                  <input
                    className="field-input"
                    type="number"
                    min={0}
                    value={p.capacity ?? ""}
                    onFocus={(e) => {
                      editOrig.current[p.id + ":capacity"] = e.target.value;
                    }}
                    onChange={(e) =>
                      setPlaces((arr) =>
                        arr.map((x) =>
                          x.id === p.id ? { ...x, capacity: e.target.value === "" ? null : Number(e.target.value) } : x,
                        ),
                      )
                    }
                    onBlur={(e) => {
                      if (e.target.value !== editOrig.current[p.id + ":capacity"]) {
                        patchPlace(p.id, { capacity: e.target.value === "" ? null : Number(e.target.value) });
                      }
                    }}
                  />
                  <input
                    className="field-input"
                    placeholder={t("places.field.notes", { fallback: "Notes" })}
                    value={p.notes || ""}
                    onFocus={(e) => {
                      editOrig.current[p.id + ":notes"] = e.target.value;
                    }}
                    onChange={(e) =>
                      setPlaces((arr) => arr.map((x) => (x.id === p.id ? { ...x, notes: e.target.value } : x)))
                    }
                    onBlur={(e) => {
                      if (e.target.value !== editOrig.current[p.id + ":notes"]) {
                        patchPlace(p.id, { notes: e.target.value || null });
                      }
                    }}
                  />
                  <label style={{ fontSize: FS.s10, color: "var(--tm)", display: "flex", alignItems: "center", gap: 4 }}>
                    <input
                      type="checkbox"
                      checked={!!p.archived}
                      onChange={(e) => patchPlace(p.id, { archived: e.target.checked })}
                    />
                    {t("places.archived", { fallback: "Arch." })}
                  </label>
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <label
                      className="btn btn-ghost"
                      style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                      title={p.photo ? t("places.replacePhoto", { fallback: "Change picture" }) : t("places.uploadPhoto", { fallback: "Upload a picture" })}
                    >
                      {p.photo ? (
                        <img
                          src={p.photo}
                          alt=""
                          style={{ width: 16, height: 16, objectFit: "cover", borderRadius: RAD.r3 }}
                        />
                      ) : (
                        <Upload size={12} />
                      )}
                      {t("places.picture", { fallback: "Picture" })}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        style={{ display: "none" }}
                        onChange={(e) => onPhoto(p, e)}
                      />
                    </label>
                    {p.photo && (
                      <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => patchPlace(p.id, { photo: null })}
                        title={t("places.removePhoto", { fallback: "Remove picture" })}
                        style={{ padding: "4px 6px" }}
                      >
                        <X size={11} />
                      </button>
                    )}
                    <button
                      className="btn btn-ghost"
                      type="button"
                      onClick={() => setEditing(p)}
                      title={t("places.editAvailability", { fallback: "Edit availability" })}
                    >
                      {t("places.availability", { fallback: "Availability" })}
                    </button>
                    <button
                      className="btn btn-danger"
                      type="button"
                      onClick={() => deletePlace(p.id)}
                      title={t("common.delete", { fallback: "Delete" })}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {editing && (
        <BaseAvailabilityEditor
          place={editing}
          value={editing.baseAvailability}
          onClose={() => setEditing(null)}
          onSave={async (normalized: any) => {
            await patchPlace(editing.id, { baseAvailability: normalized });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
